import { CalendarRole } from './fmAuthorization';

/**
 * F9 external-link builders for the FM dashboard.
 *
 * Every destination here is owned by ANOTHER team — Resource Search by the `skillsearch` module,
 * the resource calendar by the `resourcedashboard` module, the profile page by OneProfile/ESXP.
 * Prefer FXP state-name navigation (`fmNavigation.ts`); the URL templates below are only the
 * fallback used when the FXP shell can't resolve the state (local preview / tests).
 *
 * Legacy references:
 * - Resource Search   `fmdashboard.controller.js:41-43` → state `grm-skillsearch` (`/skillsearch`)
 * - Resource Calendar `fmdashboard.controller.js:204-216` → state `grm-resourcedashboardfm`
 *   (`/resourcedashboard/:alias/:role/:managerAlias`)
 * - Profile           `fmforecastview.html:72` `<source-link linktype="ONEPROFILERESOURCEPAGE">`
 *   → `sourceLinkConfig.ONEPROFILERESOURCEPAGE.BaseURL + "/" + alias` (`grm.config.*.json`)
 */

/** FXP state names owned by the GRM partner app. Navigate by NAME, never by hardcoded URL. */
export const RESOURCE_SEARCH_STATE = 'grm-skillsearch';
export const RESOURCE_CALENDAR_STATE = 'grm-resourcedashboardfm';

/** Fallback URLs, used only when the FXP shell cannot resolve the state above. */
export const RESOURCE_SEARCH_HREF = '#/skillsearch';
const RESOURCE_CALENDAR_TEMPLATE = '#/resourcedashboard/{alias}/{role}/{manager}';
const PROFILE_PATH = '#/profile/{alias}';

export interface ResourceCalendarOptions {
  /** FM/manager alias — the legacy route's trailing segment. */
  managerAlias?: string;
  /** `ResourceManager` or `FunctionalManager`, derived from the user's claims. */
  role?: CalendarRole;
}

export interface ResourceCalendarLink {
  /** Params for `grm-resourcedashboardfm`. */
  params: { alias: string; role: CalendarRole; managerAlias: string };
  /** Fallback URL used only if the shell can't resolve the state. */
  fallbackUrl: string;
}

/**
 * Build the resource-calendar deep link, or `undefined` when it cannot be built correctly.
 *
 * Returning `undefined` is deliberate and load-bearing: omitting the `managerAlias` segment
 * produces `/resourcedashboard/:alias/:role`, which matches a DIFFERENT registered state
 * (`grm-resourcedashboardquicksearch`) and silently lands the user on the wrong screen. Likewise
 * a missing `role` previously defaulted to `ResourceManager` for every user, including functional
 * managers who never held that role. Callers must hide the link when this returns `undefined`.
 */
export function resourceCalendarLink(
  alias: string,
  options: ResourceCalendarOptions = {}
): ResourceCalendarLink | undefined {
  const { managerAlias, role } = options;
  if (!alias || !managerAlias || !role) {
    return undefined;
  }
  const fallbackUrl = RESOURCE_CALENDAR_TEMPLATE.replace('{alias}', encodeURIComponent(alias))
    .replace('{role}', role)
    .replace('{manager}', encodeURIComponent(managerAlias));
  return { params: { alias, role, managerAlias }, fallbackUrl };
}

/**
 * Resource profile page (parity: "open the profile of the resource … in new tab").
 * `baseUrl` is the environment's ESXP base (`RAvailUrlProvider.esxpBaseUrl()`), mirroring the
 * legacy per-environment `sourceLinkConfig` rather than hardcoding a host.
 */
export function resourceProfileHref(alias: string, baseUrl?: string): string {
  const path = PROFILE_PATH.replace('{alias}', encodeURIComponent(alias));
  if (!baseUrl) {
    return path;
  }
  return `${baseUrl.replace(/\/+$/, '')}/${path}`;
}
